import { useEffect, useRef, type RefObject } from "react";

const FOCUSABLE = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/** The focused element, looking through shadow roots (the launcher, the widget host). */
const deepActiveElement = (): HTMLElement | null => {
  let active = document.activeElement as HTMLElement | null;
  while (active?.shadowRoot?.activeElement) {
    active = active.shadowRoot.activeElement as HTMLElement;
  }
  return active;
};

/**
 * The open in-game panel behaves as a modal dialog for keyboard and screen-reader users:
 *
 * - **Focus moves in** on open (to the panel itself, which carries the dialog's name) and
 *   **back** to whatever opened it on close — usually the launcher pill.
 * - **Tab cycles** inside the layer (panel, close, campaign selector) instead of walking into the
 *   game behind the scrim.
 * - **Escape closes** the widget.
 *
 * Escape is the LAST in line: the campaign selectors swallow it in the capture phase to fold
 * themselves first, and while a dialog inside the widget is open (leaderboard, eligible games,
 * a reveal) this hook stands aside so Escape closes only that dialog. Listening on the document,
 * not on the layer, catches the key wherever focus is — including on `<body>` after a tap.
 */
export const useInGameModal = ({
  active,
  layerRef,
  panelRef,
  onClose,
}: {
  active: boolean;
  /** The open layer: everything Tab may reach. */
  layerRef: RefObject<HTMLElement | null>;
  /** The panel: `role="dialog"`, `tabIndex={-1}` — where focus lands on open. */
  panelRef: RefObject<HTMLElement | null>;
  onClose: () => void;
}) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!active) return;
    const returnTo = deepActiveElement();
    // After the layer has mounted (the effect runs in the same commit, the ref may still be
    // settling inside AnimatePresence). A timer, not rAF: rAF stalls in background tabs.
    const focusTimer = window.setTimeout(
      () => panelRef.current?.focus({ preventScroll: true }),
      0
    );

    const root = (): ShadowRoot | Document | null => {
      const node = layerRef.current?.getRootNode();
      return node instanceof ShadowRoot || node instanceof Document
        ? node
        : null;
    };
    // Any dialog inside the widget other than the panel itself. Radix dialogs stay mounted while
    // they animate out with `data-state="closed"` — that one is already gone as far as the player
    // is concerned, so the next Escape is the panel's.
    const nestedDialogOpen = () =>
      [...(root()?.querySelectorAll('[role="dialog"]') ?? [])].some(
        (el) =>
          el !== panelRef.current && el.getAttribute("data-state") !== "closed"
      );

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || nestedDialogOpen()) return;

      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;

      const layer = layerRef.current;
      if (!layer) return;
      const items = [...layer.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.getClientRects().length > 0 && !el.closest("[inert]")
      );
      const current = (root()?.activeElement ?? null) as HTMLElement | null;
      if (items.length === 0) {
        e.preventDefault();
        panelRef.current?.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (!current || !layer.contains(current)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (
        e.shiftKey &&
        (current === first || current === panelRef.current)
      ) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && current === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      if (returnTo?.isConnected) returnTo.focus({ preventScroll: true });
    };
  }, [active, layerRef, panelRef]);
};
